<template>
    <modal-component
      :active="active"
      @cancel="close"
      @hdlOnClick="updateStatus"
      textSave="Guardar"
      :titleModal="isEditingDocx ? 'Actualizar plantilla Word' : 'Agregar plantilla'"
      :disabled="loading"
      :loader="loading"
      widthModal="width: 90%"
    >
      <div class="columns is-multiline is-centered">
        <div class="column is-7">
          <div class="field">
            <label class="label">Tipo de plantilla</label>
            <div class="control">
              <div class="select is-fullwidth" :class="{ 'is-disabled': isEditingDocx }">
                <select v-model="contentType" :disabled="isEditingDocx">
                  <option :value="null" disabled>Seleccione una opción</option>
                  <option value="docx">Archivo Word (.docx)</option>
                  <option v-if="!isEditingDocx" value="html">Archivo Html (.html)</option>
                </select>
              </div>
              <div v-if="contentType === 'docx'" class="note">
                <span>Nota:</span> El archivo de Word que subas a Intelligential debe tener ya incluidas las variables del sistema. Si aún no lo has preparado, <a href="#" @click.prevent="handleGuideClick" target="_blank" rel="noopener noreferrer">puedes descargar aquí la guía de llenado</a>.
              </div>
            </div>
          </div>
        </div>
        <div class="column is-7" v-if="contentType === 'docx'">
          <UploadInput class="-mt-2" @file-selected="handleFileSelected" accept=".docx" />
        </div>
      </div>
      <div v-show="contentType === 'html'">
        <div v-show="!showHtmlModal" class="has-text-confirmation editor-container">
          <div class="editor" ref="quillEditor"></div>
        </div>
        <div v-if="showHtmlModal" class="html-modal">
          <div class="buttons">
            <button class="button is-small" @click="saveHtmlContent">Ocultar editor html</button>
          </div>
          <textarea v-model="htmlContent" class="html-textarea"></textarea>
        </div>
      </div>
    </modal-component>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import mixins from '@/mixins/formats'
import Quill from 'quill'
import 'quill/dist/quill.snow.css'
import HtmlEditButton from 'quill-html-edit-button'

export default {
  components: {
    ModalComponent: () => import('@/components/ui/ModalComponent'),
    UploadInput: () => import('@/components/ui/UploadInput')
  },
  mixins: [mixins],
  props: {
    active: {
      type: Boolean,
      default: false
    },
    close: {
      type: Function,
      detault: () => {}
    },
    reload: {
      type: Function,
      detault: () => {}
    },
    document: {
      type: Object,
      default: null
    },
    openGuideModal: {
      type: Function,
      default: () => {}
    }
  },
  data () {
    return {
      loading: false,
      quill: null,
      htmlContent: null,
      name: null,
      contentType: null,
      showHtmlModal: false,
      wordBase64: null,
      word: null
    }
  },
  mounted () {
    this.initializeQuill()
    if (this.isEditingDocx) {
      this.contentType = 'docx'
    }
  },
  methods: {
    ...mapActions(['updateContractualDocumentTemplate']),
    async updateStatus () {
      this.loading = true
      const htmlContent = this.showHtmlModal ? this.htmlContent : this.quill.root.innerHTML
      const response = await this.updateContractualDocumentTemplate({
        contractualDocumentId: Number(this.$route.params.documentId),
        type: this.contentType,
        template: this.contentType === 'html' ? htmlContent : this.wordBase64
      })

      if (response.success) {
        await this.reload()
        this.close()
      }
      this.loading = false
    },
    openHtmlModal () {
      this.htmlContent = this.quill.root.innerHTML
      this.showHtmlModal = true
    },
    handleGuideClick () {
      this.openGuideModal('docx')
    },
    initializeQuill () {
      setTimeout(() => {
        if (Quill && HtmlEditButton) {
          Quill.register('modules/htmlEditButton', HtmlEditButton)
        }
        if (!this.$refs?.quillEditor) {
          return
        }
        if (this.quill) {
          return // Ya existe, no vuelvas a crearlo
        }
        this.quill = new Quill(this.$refs.quillEditor, {
          theme: 'snow',
          placeholder: 'Escribe tu aquí...',
          modules: {
            toolbar: {
              container: [
                ['bold', 'italic', 'underline', 'strike'],
                ['blockquote', 'code-block'],
                ['link', 'image'],
                [{ header: 1 }, { header: 2 }],
                [{ list: 'ordered' }, { list: 'bullet' }],
                [{ script: 'sub' }, { script: 'super' }],
                [{ indent: '-1' }, { indent: '+1' }],
                [{ size: ['small', false, 'large', 'huge'] }],
                [{ header: [1, 2, 3, 4, 5, 6, false] }],
                [{ color: [] }, { background: [] }],
                [{ font: [] }],
                [{ align: [] }],
                ['clean'],
                ['htmlButton'] // 👈 Aquí pones el botón personalizado
              ],
              handlers: {
                htmlButton: () => {
                  this.openHtmlModal()
                }
              }
            }
          }
        })
        if (this.item?.content) {
          this.quill.clipboard.dangerouslyPasteHTML(this.item.content)
        }
        this.quill.getModule('toolbar')
        const button = document.querySelector('.ql-htmlButton')
        if (button) {
          button.innerHTML = '<i class="fas fa-code"></i>' // Puedes poner tu icono o texto
        }
      }, 100)
    },
    saveHtmlContent () {
      if (this.quill && this.htmlContent !== null) {
        this.quill.clipboard.dangerouslyPasteHTML(this.htmlContent)
      }
      this.showHtmlModal = false
    },
    handleFileSelected (file) {
      if (!file) {
        return
      }
      const isDocx = file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                 file.name.toLowerCase().endsWith('.docx')

      if (!isDocx) {
        return
      }

      const reader = new FileReader()
      reader.onload = (e) => {
        this.wordBase64 = e.target.result
        this.word = file
      }
      reader.readAsDataURL(file)
    }
  },
  computed: {
    ...mapState({
      systemConfiguration: state => state.configuration,
      profile: state => state.financialCompany.financialCompany
    }),
    isEditingDocx () {
      return this.document && this.document.type === 'docx'
    }
  }
}
</script>

<style lang="scss" scoped>
.pay-content {
  animation: 0.5s pay_content_effet;
  @keyframes pay_content_effet {
    0% {
      opacity: 0;
    }
    80% {
      opacity: 1;
    }
  }
}
.note {
  font-size: 12px;
  display: block;
  margin-top: 1rem;
  span {
    font-weight: 600;
  }
  a {
    font-weight: 600;
    color: #007bff;
    cursor: pointer;
    text-decoration: underline;

    &:hover {
      color: #0056b3;
    }
  }
}

.select.is-disabled {
  opacity: 0.6;
  pointer-events: none;
}
</style>
